import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import type { TableColumnsType } from 'antd';
import { Alert, Button, Input, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import WmsDownloadModal from '../../../../components/WmsDownloadModal';
import ParcelDetailDrawer from '../../../delivery/parcels/components/ParcelDetailDrawer';
import { getUserInfo } from '../../../../utils/auth';
import { extractGraphQLMessageKey } from '../../../../utils/graphqlUtils';
import type { BagShipmentNodeKind, BagShipmentNodeType } from '../graphql/queries/getBagShipmentHistory';
import {
    GET_BAG_SHIPMENT_SNAPSHOT,
    type BagShipmentSnapshotParcelType,
    type GetBagShipmentSnapshotResponse,
    type GetBagShipmentSnapshotVariables,
} from '../graphql/queries/getBagShipmentSnapshot';
import {
    GENERATE_BAG_SHIPMENT_SNAPSHOT_DOWNLOAD_REPORT,
    type GenerateBagShipmentSnapshotDownloadReportResponse,
    type GenerateBagShipmentSnapshotDownloadReportVariables,
} from '../graphql/mutations/generateBagShipmentSnapshotDownloadReport';

// 与 api-server 的 MessageKey.DOWNLOAD_EXCEEDS_LIMIT 保持一致
const DOWNLOAD_EXCEEDS_LIMIT_KEY = 'errors.business.download.exceedsLimit';

// 后端时间字段是 ISO-8601 字符串；通用 formatTimestampToDateTime 对 ISO 会返回 Invalid Date
const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '-';
    const d = dayjs(iso);
    return d.isValid() ? d.format('YYYY/MM/DD HH:mm:ss') : '-';
};

interface BagShipmentSnapshotPanelProps {
    bagNumber: string;
    nodes: BagShipmentNodeType[];
    selectedNodeId: string | null;
    onSelectNode: (nodeId: string) => void;
}

const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];
const DEFAULT_PAGE_SIZE = 10;

// 与「当前选中节点」同生命周期的查询状态：换节点即整份作废，见组件内 effectiveState 的说明
interface NodeScopedQueryState {
    nodeId: string | null;
    page: number;
    // 服务端筛选：null=不筛选（默认走后端 BOUND），'IN_BAG'/'OUT_OF_BAG' 传给后端过滤
    inBagStatus: 'IN_BAG' | 'OUT_OF_BAG' | null;
    // 服务端筛选：null=不筛选；数组=按选中的 OverallStatus 传给后端
    orderStatuses: string[] | null;
    // 服务端精准匹配：null=不筛选；非空字符串=传给后端做 tracking_number 等值查询
    trackingNumber: string | null;
}

const INITIAL_NODE_SCOPED_STATE: Omit<NodeScopedQueryState, 'nodeId'> = {
    page: 1,
    inBagStatus: null,
    orderStatuses: null,
    trackingNumber: null,
};

// 「运单状态」筛选选项：固定完整枚举，避免"选完当前页就没选项"的问题
// 这里的值会原样传给后端与 order.overallStatus.name 做字符串比对，必须与后端枚举名一致；
// 旧名 PENDING_SHIPMENT 已改名 FEEDER_PENDING_SHIPMENT，保留旧名会得到一个永远 0 条的死选项，故直接移除
const ORDER_STATUS_FILTER_OPTIONS: string[] = [
    'CREATED', 'ASSIGNED', 'ARRIVED_ORIGIN_HUB',
    'LINEHAUL_PENDING_SHIPMENT', 'FEEDER_PENDING_SHIPMENT', 'HANDED_OVER',
    'IN_TRANSIT', 'ARRIVED_DESTINATION_HUB', 'ARRIVED_WHS',
    'WAITING_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS', 'PRE_DELIVERED', 'DELIVERED',
    'DELIVERY_FAILED', 'PENDING_INTERCEPTION', 'INTERCEPTED', 'HOLDING',
    'RETURN_STARTED', 'RETURN_ARRIVED_DESTINATION_HUB', 'RETURNED',
    'PICKUP_FAILED', 'DESTROYED', 'MISSING', 'DAMAGED', 'CANCELLED',
];

const BagShipmentSnapshotPanel: React.FC<BagShipmentSnapshotPanelProps> = ({
    bagNumber,
    nodes,
    selectedNodeId,
    onSelectNode,
}) => {
    const { t } = useTranslation();
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [downloadFileName, setDownloadFileName] = useState('');
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');

    // 分页与筛选都归属于「当前选中的节点」：节点一变（左侧时间轴点击 / 右侧下拉切换），
    // 整份状态在渲染期派生回初始值（第 1 页 + 无筛选）。
    // 不用 useEffect 事后纠正，是因为那样会先带旧页码/旧筛选发一次请求——后端对越界页不做 clamp，
    // 直接返回空列表（表现为「切到数据少的节点就整页空白」，本次修复的线上问题），
    // 还会多一次无效 DB 查询并让表头筛选图标闪一下高亮。
    // pageSize 不在此列：它是用户显式选择的展示偏好，跨节点保留。
    const [nodeScopedState, setNodeScopedState] = useState<NodeScopedQueryState>({
        nodeId: selectedNodeId,
        ...INITIAL_NODE_SCOPED_STATE,
    });
    const effectiveState = nodeScopedState.nodeId === selectedNodeId
        ? nodeScopedState
        : { nodeId: selectedNodeId, ...INITIAL_NODE_SCOPED_STATE };
    // 派生之外还必须把重置结果写回 state：只派生的话 nodeScopedState 会一直留着上一个节点的值，
    // A(第 3 页) → B(未做任何翻页/筛选，例如 CREATED 节点本就被 skip 不发请求) → 再切回 A 时
    // nodeId 又重新相等，page=3 与旧筛选原样复活并带进请求（不是空白页，但与「切节点回第 1 页」不符）。
    // 放在渲染期而非 useEffect：React 会丢弃本次渲染输出并立刻用新 state 重渲染，
    // 而 useEffect 要等提交后才跑，那时旧页码的请求已经发出去了。
    // 本次渲染仍会执行到底（只是输出被丢弃），所以查询参数一律用 effectiveState、
    // 不能用尚未更新的 nodeScopedState——否则 useQuery 会先按旧页码发一次越界请求。
    if (nodeScopedState.nodeId !== selectedNodeId) {
        setNodeScopedState(effectiveState);
    }
    // patch 基于派生后的 effectiveState 展开，保证节点刚切换、state 尚未同步时不会把旧值写回
    const patchNodeScopedState = (patch: Partial<NodeScopedQueryState>) =>
        setNodeScopedState({ ...effectiveState, ...patch });
    const {
        page: currentPage,
        inBagStatus: inBagStatusFilter,
        orderStatuses: orderStatusFilter,
        trackingNumber: trackingNumberFilter,
    } = effectiveState;

    // 创建节点无快照数据，前端直接展示空态，不发 GraphQL 请求
    const selectedKind = nodes.find((n) => n.id === selectedNodeId)?.kind;
    const isCreatedNode = selectedKind === 'CREATED';

    const { data, loading, error } = useQuery<GetBagShipmentSnapshotResponse, GetBagShipmentSnapshotVariables>(
        GET_BAG_SHIPMENT_SNAPSHOT,
        {
            variables: {
                bagNumber,
                nodeId: selectedNodeId ?? '',
                pageRequest: { page: currentPage, size: pageSize },
                inBagStatus: inBagStatusFilter,
                orderStatuses: orderStatusFilter,
                trackingNumber: trackingNumberFilter,
            },
            skip: !bagNumber || !selectedNodeId || isCreatedNode,
            errorPolicy: 'all',
            fetchPolicy: 'cache-and-network',
        }
    );

    const [generateDownload, { loading: downloading }] = useMutation<
        GenerateBagShipmentSnapshotDownloadReportResponse,
        GenerateBagShipmentSnapshotDownloadReportVariables
    >(GENERATE_BAG_SHIPMENT_SNAPSHOT_DOWNLOAD_REPORT);

    const snapshot = isCreatedNode ? null : (data?.bagShipmentSnapshot ?? null);
    const parcels = snapshot?.content ?? [];

    const kindLabel = (kind: BagShipmentNodeKind): string =>
        t(`wms.bags.shipmentHistory.nodeKind.${kind}`);

    const handleDownload = async () => {
        if (!selectedNodeId) {
            message.warning(t('wms.bags.shipmentHistory.message.selectNodeFirst'));
            return;
        }
        try {
            const result = await generateDownload({
                variables: {
                    bagNumber,
                    nodeId: selectedNodeId,
                    inBagStatus: inBagStatusFilter,
                    orderStatuses: orderStatusFilter,
                    trackingNumber: trackingNumberFilter,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            });
            const path = result.data?.generateBagShipmentSnapshotDownloadReport;
            if (!path) {
                message.error(t('wms.bags.shipmentHistory.message.downloadFailed'));
                return;
            }
            setDownloadFileName(path);
            setDownloadModalVisible(true);
        } catch (e) {
            // 仅「条数超限」透出后端的具体原因：该 messageKey 三语齐备，且 details 带 maxLimit，
            // 用户据此知道该缩小筛选范围。其余错误仍用通用文案——后端 message 中英混杂
            // （重复提交为英文），非业务异常还会是 Internal server error，都不宜直接展示。
            if (extractGraphQLMessageKey(e) === DOWNLOAD_EXCEEDS_LIMIT_KEY) {
                // 只取词条实际用到的 maxLimit：传 Record<string, unknown> 会让 i18next 的
                // t() 重载解析到 returnDetails 分支（返回 TFunctionDetailedResult 而非 string）
                const details = (e as any)?.graphQLErrors?.[0]?.extensions?.details as
                    | { maxLimit?: number }
                    | undefined;
                message.error(t(DOWNLOAD_EXCEEDS_LIMIT_KEY, { maxLimit: details?.maxLimit }));
                return;
            }
            message.error(t('wms.bags.shipmentHistory.message.downloadFailed'));
        }
    };

    const orderStatusFilters = useMemo(
        () => ORDER_STATUS_FILTER_OPTIONS.map((s) => ({ text: t(`enum.overallStatus.${s}`, s), value: s })),
        [t]
    );

    const columns: TableColumnsType<BagShipmentSnapshotParcelType> = [
        {
            title: t('wms.bags.shipmentHistory.column.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            filteredValue: trackingNumberFilter ? [trackingNumberFilter] : null,
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder={t('wms.bags.shipmentHistory.column.trackingNumber') as string}
                        value={selectedKeys[0] as string | undefined}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: 'block', width: 200 }}
                        allowClear
                    />
                    <Space size="small">
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => confirm()}
                        >
                            {t('components.button.search', 'Search')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                clearFilters?.();
                                confirm();
                            }}
                        >
                            {t('components.button.reset', 'Reset')}
                        </Button>
                    </Space>
                </div>
            ),
            render: (text: string, record: BagShipmentSnapshotParcelType) =>
                record.orderId != null ? (
                    <Button
                        type="link"
                        onClick={() => {
                            setSelectedOrderId(String(record.orderId));
                            setParcelDetailVisible(true);
                        }}
                        style={{ padding: 0, height: 'auto' }}
                    >
                        {text}
                    </Button>
                ) : (
                    <span>{text}</span>
                ),
        },
        {
            title: t('wms.bags.shipmentHistory.column.inBagStatus'),
            dataIndex: 'inBagStatus',
            key: 'inBagStatus',
            filters: [
                { text: t('wms.bags.shipmentHistory.inBagStatus.IN_BAG'), value: 'IN_BAG' },
                { text: t('wms.bags.shipmentHistory.inBagStatus.OUT_OF_BAG'), value: 'OUT_OF_BAG' },
            ],
            filterMultiple: false,
            filteredValue: inBagStatusFilter ? [inBagStatusFilter] : null,
            render: (s: 'IN_BAG' | 'OUT_OF_BAG') => (
                <Tag color={s === 'IN_BAG' ? 'green' : 'default'}>
                    {t(`wms.bags.shipmentHistory.inBagStatus.${s}`)}
                </Tag>
            ),
        },
        {
            title: t('wms.bags.shipmentHistory.column.orderStatus'),
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            filters: orderStatusFilters,
            filterSearch: true,
            filteredValue: orderStatusFilter,
            render: (status: string) => (status ? t(`enum.overallStatus.${status}`, status) : '-'),
        },
        {
            title: t('wms.bags.shipmentHistory.column.inBagAt'),
            dataIndex: 'inBagAt',
            key: 'inBagAt',
            render: (v: string | null) => formatDateTime(v),
        },
        {
            title: t('wms.bags.shipmentHistory.column.outBagAt'),
            dataIndex: 'outBagAt',
            key: 'outBagAt',
            render: (v: string | null) => formatDateTime(v),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Typography.Text strong style={{ display: 'block' }}>
                {t('wms.bags.shipmentHistory.snapshotTitle')}
            </Typography.Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Select
                    value={selectedNodeId ?? undefined}
                    onChange={onSelectNode}
                    style={{ flex: 1, minWidth: 240 }}
                    placeholder={t('wms.bags.shipmentHistory.selectNodePlaceholder')}
                    options={nodes.map((n) => ({
                        value: n.id,
                        label: `${kindLabel(n.kind)} · ${formatDateTime(n.occurredAt)}`,
                    }))}
                />
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    loading={downloading}
                    disabled={!selectedNodeId}
                >
                    {t('wms.bags.shipmentHistory.downloadButton')}
                </Button>
            </div>

            {error && (
                <Alert
                    message={t('wms.bags.shipmentHistory.message.snapshotLoadFailed')}
                    description={error.message}
                    type="error"
                    showIcon
                />
            )}

            {loading && !data ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                    <Spin />
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={parcels}
                    rowKey="trackingNumber"
                    size="middle"
                    onChange={(_pagination, filters) => {
                        const rawInBag = filters.inBagStatus;
                        const nextInBag = Array.isArray(rawInBag) && rawInBag.length > 0 ? (rawInBag[0] as 'IN_BAG' | 'OUT_OF_BAG') : null;
                        const rawOrder = filters.orderStatus;
                        const nextOrder = Array.isArray(rawOrder) && rawOrder.length > 0 ? (rawOrder as string[]) : null;
                        const rawTracking = filters.trackingNumber;
                        const nextTracking = Array.isArray(rawTracking) && rawTracking.length > 0
                            ? String(rawTracking[0]).trim() || null
                            : null;
                        const inBagChanged = nextInBag !== inBagStatusFilter;
                        const orderChanged = JSON.stringify(nextOrder) !== JSON.stringify(orderStatusFilter);
                        const trackingChanged = nextTracking !== trackingNumberFilter;
                        // 翻页同样会触发 onChange，此时三个筛选都没变，不能误把页码重置回 1
                        if (inBagChanged || orderChanged || trackingChanged) {
                            patchNodeScopedState({
                                page: 1,
                                inBagStatus: nextInBag,
                                orderStatuses: nextOrder,
                                trackingNumber: nextTracking,
                            });
                        }
                    }}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: snapshot?.totalElements ?? 0,
                        onChange: (page, size) => {
                            patchNodeScopedState({ page: size !== pageSize ? 1 : page });
                            if (size !== pageSize) setPageSize(size);
                        },
                        showSizeChanger: true,
                        pageSizeOptions: PAGE_SIZE_OPTIONS,
                        showQuickJumper: false,
                        showTotal: (total) => t('table.pagination.totalItems', { total }),
                        size: 'small',
                    }}
                />
            )}

            <WmsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={downloadFileName}
            />

            {parcelDetailVisible && (
                <ParcelDetailDrawer
                    visible={parcelDetailVisible}
                    onClose={() => setParcelDetailVisible(false)}
                    orderId={selectedOrderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                />
            )}
        </div>
    );
};

export default BagShipmentSnapshotPanel;
